import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  eventImage,
  formatDateParts,
  getHeaderImage,
  getBtvFallbackForBg,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Dolphin = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  mode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const baseBg = mode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[mode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const secondaryAccent = resolveColor(themeColors.secondaryAccentColor, customizations?.secondaryAccentColor);
  const originalHeading = customizations?.headingTextColor || parseColor(themeColors.headingTextColor);
  const originalBody = customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor);
  const originalButtonTextColor =
    customizations?.buttonTextColor || parseColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor);

  const heading = getRelativeContrastColor(originalHeading, accent);
  const body = getRelativeContrastColor(originalBody, container);
  const buttonTextColor = getRelativeContrastColor(originalButtonTextColor, secondaryAccent);

  const sessionParts = event.sessions[0]?.timestamp ? formatDateParts(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(container);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Georgia, serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Trebuchet MS, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || "You've been invited to");
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Dolphin"
      width={520}
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--secondary-accent': secondaryAccent,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonTextColor,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
          width: '100%',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)' }}
            >
              <tbody>
                {/* Header image */}
                <tr>
                  <td
                    align="center"
                    valign="middle"
                    height={48}
                    style={{ background: 'var(--container)', padding: '8px' }}
                  >
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                {/* "You've been invited to" band */}
                <tr>
                  <td align="center" style={{ background: 'var(--accent)', padding: '24px 16px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '32px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 400,
                        lineHeight: '32px',
                        letterSpacing: '0.96px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>
                {/* Image band */}
                <tr>
                  <td align="center" style={{ background: 'var(--accent)', fontSize: 0, lineHeight: 0 }}>
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={248} src={bodyImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                {/* Title band */}
                <tr>
                  <td align="center" style={{ background: 'var(--accent)', padding: '24px 16px' }}>
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '48px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 400,
                        textTransform: 'uppercase',
                        lineHeight: '48px',
                        letterSpacing: '1.44px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>
                {/* Brief info */}
                {eventDescription && (
                  <tr>
                    <td align="center" style={{ padding: '24px 40px 0 40px' }}>
                      <ContentEditable
                        value={eventDescription}
                        setValue={setDescription}
                        style={{
                          color: 'var(--body)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 400,
                          lineHeight: '16.8px',
                          wordWrap: 'break-word',
                          textAlign: 'center',
                          maxWidth: '317px',
                        }}
                      />
                    </td>
                  </tr>
                )}
                {/* Date section */}
                {sessionParts && (
                  <tr>
                    <td align="center" style={{ padding: '24px 40px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td align="center" style={{ padding: '16px 24px' }}>
                              <div style={{ textAlign: 'center', width: '98px' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    lineHeight: '14px',
                                    letterSpacing: '0.42px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.dayName}
                                </div>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '32px',
                                    fontFamily: 'var(--heading-font)',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    lineHeight: '32px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.shortDate}
                                </div>
                              </div>
                            </td>
                            <td
                              style={{
                                padding: '16px 8px',
                                borderTop: '1px solid var(--body)',
                                width: '38px',
                                fontSize: 0,
                                lineHeight: 0,
                              }}
                            >
                              &nbsp;
                            </td>
                            <td align="center" style={{ padding: '16px 24px' }}>
                              <div style={{ textAlign: 'center', width: '98px' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    lineHeight: '14px',
                                    letterSpacing: '0.42px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.period}
                                </div>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '32px',
                                    fontFamily: 'var(--heading-font)',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    lineHeight: '32px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.time}
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                {/* Register button */}
                <tr>
                  <td align="center" style={{ paddingBottom: '40px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{ background: 'var(--secondary-accent)', width: '148px', height: '40px' }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()} // do here to prevent nav in editor
                              style={{
                                textDecoration: 'none',
                                padding: '6px 0 6px 0',
                                width: '148px',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '13px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  lineHeight: '16px',
                                  wordWrap: 'break-word',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
            {/* Calendar links footer */}
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
